import 'url-search-params-polyfill';
import {
  AddToShoppingCartContext,
  AddToShoppingCartSource,
  CartEventType,
  FreeTrialStartedFromOriginEnum,
  ICustomer,
  IOrderItem,
  IPromotionCode,
  ISDKKlaviyo,
  PaymentMethodType,
  ShoppingCartProductType,
} from '@monofront/types';
import { trackEvent } from '@monofront/tracking';
import { getLocationFromSessionStorage } from '@monofront/business/utils';
import { CHURNER, FREE_TRIALER, MEMBER } from '@monofront/business/constants';
import { dataLayerTrackEvent, pushDataLayer } from './googleTagManager';
import { bingTrackEvent, fireBingPageView } from './bing';
import { Experiment, Result } from '@monofront/business/contexts';
import { getAuth } from '@firebase/auth';

export * from './googleTagManager';
export * from './facebook';
export * from './bing';
export * from './klaviyo';

declare global {
  interface Window {
    uetq: Array<unknown>;
    fbq: any;
    klaviyo?: ISDKKlaviyo;
    lfMockKlaviyo: Array<any>;
    _uxa: any;
  }
}

const TRACKING_SHOPPING_CART_EVENT_ACTION = {
  addToCart: 'add',
  removeFromCart: 'remove',
};

// @deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
export const computeCustomerType = (
  customer?: ICustomer | null,
  isMember?: boolean,
): string | null => {
  return customer?.omsTags?.includes(CHURNER)
    ? CHURNER
    : customer?.omsTags?.includes(FREE_TRIALER)
    ? FREE_TRIALER
    : isMember
    ? MEMBER
    : null;
};

// @deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
export const trackShoppingCartUpdate = (
  event: CartEventType,
  shoppingCartProduct: ShoppingCartProductType[] | IOrderItem[],
  context: AddToShoppingCartContext = {
    source: AddToShoppingCartSource.DEFAULT,
  },
  currencyCode = 'EUR',
) => {
  const cartEvent = {
    ecommerce: {
      currencyCode,
      [TRACKING_SHOPPING_CART_EVENT_ACTION[event]]: {
        products: [...shoppingCartProduct.map(formatGAShoppingCartProduct)],
      },
    },
  };

  if (event === 'addToCart' && context.source.length > 0) {
    cartEvent['addToCartType'] = context.source;
  }

  if (event === 'addToCart' && context.widgetName && context.widgetPosition) {
    cartEvent[
      'positionWidget'
    ] = `${context.widgetName}|${context.widgetPosition}`;
  }

  if (event === 'removeFromCart') {
    fireAutoMLEvent('remove-from-cart', {
      productEventDetail: {
        productDetails: shoppingCartProduct?.map((product) => ({
          id: product.sku,
          quantity: product.quantity,
        })),
      },
    });
  }

  dataLayerTrackEvent(event, cartEvent);
};

/**
 * Trigger event for initialization of Google Optimize tests
 */
export const fireGoogleOptimize = () => {
  pushDataLayer({
    event: 'custom.beforepageView',
  });
};

export const firePageView = () => {
  const originalLocation = getLocationFromSessionStorage();

  dataLayerTrackEvent('custom.pageView', {
    originalLocation: originalLocation
      ? JSON.parse(originalLocation).location
      : null,
  });
  fireBingPageView();
};

export const fireAutoMLEvent = (
  eventType: string,
  data?: Record<string, any>,
) => {
  const user = getAuth().currentUser;

  pushDataLayer({
    automl: {
      eventType,
      ...(user?.uid && {
        userInfo: {
          userId: `${user.uid}`,
        },
      }),
      ...data,
    },
  });
};

export const formatGAShoppingCartProduct = (
  shoppingCartProduct: ShoppingCartProductType | IOrderItem,
) => ({
  id: shoppingCartProduct.sku,
  name: shoppingCartProduct.title,
  brand: shoppingCartProduct.vendor,
  price: shoppingCartProduct.price,
  quantity: shoppingCartProduct.quantity,
  category: shoppingCartProduct.product_type,
});

export const trackFreeTrialStarted = ({
  subscriptionId,
  email,
  promotionCode,
  freeTrialStartType,
  isMembershipAsAProduct = false,
  freeTrialPaymentMethod,
}: {
  subscriptionId?: string;
  email?: string | null;
  promotionCode?: IPromotionCode | null;
  freeTrialStartType?: FreeTrialStartedFromOriginEnum;
  isMembershipAsAProduct?: boolean;
  freeTrialPaymentMethod: PaymentMethodType | 'unknown';
}) => {
  // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
  dataLayerTrackEvent('freeTrialStarted', {
    subscriptionId,
    subscriptionCoupon: {
      id: promotionCode?.id,
      name: promotionCode?.code,
    },
    subscriptionEmail: email,
    isMaap: isMembershipAsAProduct,
    freeTrialStartType:
      freeTrialStartType || FreeTrialStartedFromOriginEnum.generic,
    freeTrialPaymentMethod,
  });

  trackEvent({
    type: 'LFStartFreeTrial',
    data: {
      id: subscriptionId,
      promotionCode,
      email,
      isMaap: isMembershipAsAProduct,
      freeTrialPaymentMethod,
    },
  });

  bingTrackEvent('free trial started');
};

export const trackGrowthbookExperimentViewed = (
  experiment: Experiment<any>,
  result: Result<any>,
) =>
  trackEvent({
    type: 'LFGrowthbookExperimentViewed',
    data: {
      experimentKey: experiment.key,
      experimentVariation: result.variationId,
      experimentKeyVariation: `${experiment.key}:${result.variationId}`,
    },
  });
